import * as React from 'react'
import { useRouteLoaderData } from 'react-router'
import { useEffectivePrivateTheme } from '#app/contexts/PrivateAppearanceContext'
import { type CourseTypeKey } from '#app/domains/courses/types'

type RootLoaderData = {
	requestInfo?: {
		path?: string
		userPrefs?: {
			resolvedTheme?: 'light' | 'dark'
		}
	}
	brandLogo?: {
		brandKey?: string
		href?: string
		alt?: string
	}
	brandContactEmail?: string
	brandTwitterUrl?: string
	brandTitle?: string
	brandShortName?: string
	brandDomain?: string
	brandDescription?: string
	brandVoice?: {
		voiceDescription: string
		coreAttributes: string[]
		tone: string
		sentenceStructure: string
		examples: Array<{ instead: string; say: string }>
		keywords: string[]
	}
	brandCourseTypeOptions?: CourseTypeKey[]
	brandActionConfig?: {
		disabledActions?: {
			data?: Record<string, string[]>
			display?: Record<string, string[]>
		}
	}
}

function toDarkVariant(src: string, kind: 'logo' | 'icon') {
	if (!src) return src
	if (kind === 'icon') {
		if (/logo_icon-on-dark(\.[^./]+)?$/i.test(src)) return src
		if (/logo_icon(\.[^./]+)?$/i.test(src)) {
			return src.replace(
				/logo_icon(\.[^./]+)?$/i,
				(_m, ext) => `logo_icon-on-dark${ext ?? '.png'}`,
			)
		}
		return src
	}

	if (/logo-on-dark(\.[^./]+)?$/i.test(src)) return src
	if (/logo(\.[^./]+)?$/i.test(src)) {
		return src.replace(/logo(\.[^./]+)?$/i, (_m, ext) => `logo-on-dark${ext ?? '.png'}`)
	}
	return src
}

function toLightVariant(src: string) {
	return src.replace(/-on-dark(\.[^./]+)?$/i, (_m, ext) => ext ?? '.png')
}

/**
 * Hook to access brand data from root loader
 * Returns all brand-related data including logo, contact info, and metadata
 */
export function useBrandData() {
	return useRouteLoaderData('root') as RootLoaderData | undefined
}

/**
 * Hook to get brand logo with icon variant support
 * Automatically derives the icon version from the full logo path
 */
export function useBrandLogo() {
	const rootData = useBrandData()
	const brandLogo = rootData?.brandLogo
	const privateTheme = useEffectivePrivateTheme()
	const requestPath = rootData?.requestInfo?.path ?? ''
	const isPrivateRoute = /^\/(learn|settings|admin)(?:\/|$)/.test(requestPath)
	const activeTheme = isPrivateRoute
		? rootData?.requestInfo?.userPrefs?.resolvedTheme ?? privateTheme
		: 'light'
	const isDarkTheme = activeTheme === 'dark'

	const baseLogoSrc = React.useMemo(() => {
		if (brandLogo?.href) return brandLogo.href
		return brandLogo?.brandKey ? '/assets/site/images/logo.png' : '/img/logo.png'
	}, [brandLogo?.href, brandLogo?.brandKey])

	const logoSrc = React.useMemo(() => {
		return isDarkTheme ? toDarkVariant(baseLogoSrc, 'logo') : baseLogoSrc
	}, [baseLogoSrc, isDarkTheme])

	const [resolvedLogoSrc, setResolvedLogoSrc] = React.useState(logoSrc)
	React.useEffect(() => {
		setResolvedLogoSrc(logoSrc)
	}, [logoSrc])

	const handleLogoError = React.useCallback(() => {
		setResolvedLogoSrc(toLightVariant(baseLogoSrc))
	}, [baseLogoSrc])

	const fallbackIconSrc = React.useMemo(() => {
		if (!brandLogo?.brandKey) return '/img/logo_icon.png'
		const href = brandLogo.href ?? '/assets/site/images/logo.png'
		const ext = href.match(/\.[a-zA-Z0-9]+$/)?.[0] ?? '.png'
		return `/assets/site/images/logo_icon${ext}`
	}, [brandLogo])

	const inferredIconSrc = React.useMemo(() => {
		const iconCandidate = fallbackIconSrc
		return isDarkTheme ? toDarkVariant(iconCandidate, 'icon') : iconCandidate
	}, [fallbackIconSrc, isDarkTheme])

	const [iconSrc, setIconSrc] = React.useState(inferredIconSrc)
	React.useEffect(() => {
		setIconSrc(inferredIconSrc)
	}, [inferredIconSrc])

	const handleIconError = React.useCallback(() => {
		const fallback = toLightVariant(fallbackIconSrc)
		setIconSrc(fallback)
	}, [fallbackIconSrc])

	return {
		logoSrc: resolvedLogoSrc,
		handleLogoError,
		iconSrc,
		handleIconError,
		brandLogo,
	}
}
